import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  X,
  Database,
  Search,
  RefreshCw,
  Copy,
  Check,
  ChevronRight,
  ChevronDown,
  ChevronLeft,
  Loader2,
  AlertCircle,
  ArrowUpDown,
  Braces,
  FileJson,
  Calendar,
  Trash2,
  Plus,
  Sparkles,
  FolderOpen,
  Layers,
} from 'lucide-react';
import { PrismLight as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import jsonLang from 'react-syntax-highlighter/dist/esm/languages/prism/json';
import {
  analyticsHubService,
  type SemanticDataDetailRecord,
  type SemanticDataRecord,
} from '../../../../../components/api/analyticsHubService';
import { useToast } from '../../../../../components/common/Toast';
import type { Theme, ThemeColors } from './types';
import { validateEntityJson } from './utils';

SyntaxHighlighter.registerLanguage('json', jsonLang);

const SAMPLE_SEMANTIC_DATA_JSON = `{
  "total": 100,
  "region": "EMEA"
}`;

const THEME_CONFIG: Record<Theme, ThemeColors> = {
  dark: {
    primary: 'bg-neutral-900',
    secondary: 'bg-neutral-800',
    background: 'bg-neutral-950',
    surface: 'bg-neutral-800',
    border: 'border-neutral-700',
    text: { primary: 'text-white', secondary: 'text-neutral-300', muted: 'text-neutral-400' },
    accent: 'bg-indigo-600',
    hover: 'hover:bg-neutral-700',
    input: 'bg-neutral-800/80 border-neutral-600 text-white placeholder-neutral-500',
  },
  light: {
    primary: 'bg-white',
    secondary: 'bg-gray-50',
    background: 'bg-gray-100',
    surface: 'bg-white',
    border: 'border-gray-200',
    text: { primary: 'text-gray-900', secondary: 'text-gray-700', muted: 'text-gray-500' },
    accent: 'bg-indigo-600',
    hover: 'hover:bg-gray-100',
    input: 'bg-white border-gray-300 text-gray-900 placeholder-gray-400',
  },
};

type SortOption = 'newest' | 'oldest';
type ViewScope = 'document' | 'payload';
type DisplayMode = 'tree' | 'raw';
type PanelMode = 'browse' | 'add';

const DEFAULT_DETAIL_PAGE_SIZE = 50;

interface EntityGroup {
  name: string;
  records: SemanticDataRecord[];
  count: number;
  latestSubmission: string;
}

interface ReportSemanticDataModalProps {
  theme?: Theme;
  reportDbId: string;
  reportName?: string;
  suggestedEntityNames?: string[];
  onClose: () => void;
}

function formatTimestamp(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function recordMatchesSearch(record: SemanticDataRecord, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  if (record.entity_name.toLowerCase().includes(q)) return true;
  if (record._id.toLowerCase().includes(q)) return true;
  if (record.data_preview?.toLowerCase().includes(q)) return true;
  return false;
}

function sortRecords(records: SemanticDataRecord[], sort: SortOption): SemanticDataRecord[] {
  const copy = [...records];
  if (sort === 'oldest') {
    return copy.sort(
      (a, b) =>
        new Date(a.submission_datetime).getTime() - new Date(b.submission_datetime).getTime()
    );
  }
  return copy.sort(
    (a, b) =>
      new Date(b.submission_datetime).getTime() - new Date(a.submission_datetime).getTime()
  );
}

function groupRecordsByEntity(records: SemanticDataRecord[]): EntityGroup[] {
  const map = new Map<string, SemanticDataRecord[]>();
  for (const record of records) {
    const existing = map.get(record.entity_name) ?? [];
    existing.push(record);
    map.set(record.entity_name, existing);
  }

  return Array.from(map.entries())
    .map(([name, entityRecords]) => ({
      name,
      records: entityRecords,
      count: entityRecords.length,
      latestSubmission: entityRecords.reduce((latest, record) => {
        return new Date(record.submission_datetime).getTime() > new Date(latest).getTime()
          ? record.submission_datetime
          : latest;
      }, entityRecords[0].submission_datetime),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

function getDataPreview(data: unknown, maxLength = 72): string {
  try {
    const serialized = JSON.stringify(data);
    return serialized.length > maxLength ? `${serialized.slice(0, maxLength)}…` : serialized;
  } catch {
    return '{}';
  }
}

function getRecordPreview(record: SemanticDataRecord, maxLength = 72): string {
  if (record.data_preview) {
    return record.data_preview.length > maxLength
      ? `${record.data_preview.slice(0, maxLength)}…`
      : record.data_preview;
  }
  if (record.data !== undefined) {
    return getDataPreview(record.data, maxLength);
  }
  return record._id;
}

function getJsonValueType(value: unknown): string {
  if (value === null) return 'null';
  if (Array.isArray(value)) return 'array';
  return typeof value;
}

function getPreviewLabel(value: unknown): string {
  const type = getJsonValueType(value);
  if (type === 'null') return 'null';
  if (type === 'string') {
    const str = value as string;
    return str.length > 48 ? `"${str.slice(0, 48)}…"` : `"${str}"`;
  }
  if (type === 'number' || type === 'boolean') return String(value);
  if (type === 'array') return `Array(${(value as unknown[]).length})`;
  if (type === 'object') {
    const keys = Object.keys(value as Record<string, unknown>);
    return `{${keys.slice(0, 3).join(', ')}${keys.length > 3 ? ', …' : ''}}`;
  }
  return String(value);
}

interface JsonTreeNodeProps {
  label?: string;
  value: unknown;
  depth: number;
  theme: Theme;
  defaultExpanded?: boolean;
}

const JsonTreeNode: React.FC<JsonTreeNodeProps> = ({
  label,
  value,
  depth,
  theme,
  defaultExpanded = depth < 2,
}) => {
  const type = getJsonValueType(value);
  const isExpandable = type === 'object' || type === 'array';
  const [expanded, setExpanded] = useState(defaultExpanded);

  const keyColor = theme === 'dark' ? 'text-sky-300' : 'text-sky-700';
  const stringColor = theme === 'dark' ? 'text-emerald-300' : 'text-emerald-700';
  const numberColor = theme === 'dark' ? 'text-amber-300' : 'text-amber-700';
  const boolColor = theme === 'dark' ? 'text-violet-300' : 'text-violet-700';
  const nullColor = theme === 'dark' ? 'text-neutral-500' : 'text-gray-400';
  const bracketColor = theme === 'dark' ? 'text-neutral-500' : 'text-gray-500';

  const renderPrimitive = () => {
    if (type === 'string') return <span className={stringColor}>"{String(value)}"</span>;
    if (type === 'number') return <span className={numberColor}>{String(value)}</span>;
    if (type === 'boolean') return <span className={boolColor}>{String(value)}</span>;
    if (type === 'null') return <span className={nullColor}>null</span>;
    return null;
  };

  if (!isExpandable) {
    return (
      <div className="flex items-start gap-1 font-mono text-[11px] leading-relaxed py-0.5">
        {label !== undefined && (
          <>
            <span className={`${keyColor} shrink-0`}>"{label}"</span>
            <span className={bracketColor}>: </span>
          </>
        )}
        {renderPrimitive()}
      </div>
    );
  }

  const entries =
    type === 'array'
      ? (value as unknown[]).map((item, index) => [String(index), item] as const)
      : Object.entries(value as Record<string, unknown>);

  const openBracket = type === 'array' ? '[' : '{';
  const closeBracket = type === 'array' ? ']' : '}';

  return (
    <div className="font-mono text-[11px] leading-relaxed">
      <button
        type="button"
        onClick={() => setExpanded((open) => !open)}
        className="flex items-start gap-1 w-full text-left py-0.5 rounded hover:bg-white/5 transition-colors group"
      >
        <span className={`mt-0.5 shrink-0 ${bracketColor}`}>
          {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        </span>
        {label !== undefined && (
          <>
            <span className={`${keyColor} shrink-0`}>"{label}"</span>
            <span className={bracketColor}>: </span>
          </>
        )}
        <span className={bracketColor}>
          {openBracket}
          {!expanded && (
            <span className={`mx-1 ${theme === 'dark' ? 'text-neutral-600' : 'text-gray-400'}`}>
              {getPreviewLabel(value)}
            </span>
          )}
          {!expanded && closeBracket}
        </span>
        {expanded && entries.length > 0 && (
          <span className={`ml-1 text-[9px] uppercase tracking-wide ${theme === 'dark' ? 'text-neutral-600' : 'text-gray-400'}`}>
            {type === 'array' ? `${entries.length} items` : `${entries.length} fields`}
          </span>
        )}
      </button>
      {expanded && (
        <div className="border-l border-dashed border-neutral-700/60 ml-1.5 pl-3">
          {entries.map(([entryKey, entryValue]) => (
            <JsonTreeNode
              key={`${entryKey}-${depth}`}
              label={entryKey}
              value={entryValue}
              depth={depth + 1}
              theme={theme}
              defaultExpanded={defaultExpanded}
            />
          ))}
          <div className={`py-0.5 ${bracketColor}`}>{closeBracket}</div>
        </div>
      )}
      {expanded && entries.length === 0 && (
        <div className={`ml-5 py-0.5 ${bracketColor}`}>{closeBracket}</div>
      )}
    </div>
  );
};

const ReportSemanticDataModal: React.FC<ReportSemanticDataModalProps> = ({
  theme = 'dark',
  reportDbId,
  reportName,
  suggestedEntityNames = [],
  onClose,
}) => {
  const colors = THEME_CONFIG[theme];
  const { showToast } = useToast();
  const [records, setRecords] = useState<SemanticDataRecord[]>([]);
  const [collectionName, setCollectionName] = useState('semantic_data');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedEntityName, setSelectedEntityName] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedDetail, setSelectedDetail] = useState<SemanticDataDetailRecord | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [detailPage, setDetailPage] = useState(1);
  const [detailPageSize] = useState(DEFAULT_DETAIL_PAGE_SIZE);
  const [detailNonce, setDetailNonce] = useState(0);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortOption>('newest');
  const [viewScope, setViewScope] = useState<ViewScope>('document');
  const [displayMode, setDisplayMode] = useState<DisplayMode>('tree');
  const [panelMode, setPanelMode] = useState<PanelMode>('browse');
  const [treeDefaultExpanded, setTreeDefaultExpanded] = useState(true);
  const [treeKey, setTreeKey] = useState(0);
  const [copied, setCopied] = useState(false);
  const [addEntityName, setAddEntityName] = useState('');
  const [addJsonValue, setAddJsonValue] = useState(SAMPLE_SEMANTIC_DATA_JSON);
  const [addError, setAddError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const detailRequestRef = useRef(0);

  const clearDetail = useCallback(() => {
    detailRequestRef.current += 1;
    setSelectedDetail(null);
    setDetailError(null);
    setDetailLoading(false);
    setDetailPage(1);
  }, []);

  const resetAddForm = useCallback(() => {
    setAddEntityName('');
    setAddJsonValue(SAMPLE_SEMANTIC_DATA_JSON);
    setAddError(null);
  }, []);

  const loadData = useCallback(async (options?: { preserveSelection?: boolean; silent?: boolean }) => {
    const silent = options?.silent ?? false;
    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);
    try {
      const response = await analyticsHubService.getReportDefinitionSemanticData(reportDbId, {
        include_data: false,
      });
      const items = response.data?.items ?? [];
      setRecords(items);
      setCollectionName(response.data?.collection ?? 'semantic_data');
      if (options?.preserveSelection) {
        setSelectedEntityName((prevEntity) => {
          if (prevEntity && items.some((item) => item.entity_name === prevEntity)) {
            return prevEntity;
          }
          return items[0]?.entity_name ?? null;
        });
        setSelectedId((prev) => {
          if (prev && items.some((item) => item._id === prev)) return prev;
          return items[0]?._id ?? null;
        });
      } else {
        setSelectedEntityName(items[0]?.entity_name ?? null);
        setSelectedId(items[0]?._id ?? null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load semantic data.');
      setRecords([]);
      setSelectedEntityName(null);
      setSelectedId(null);
      clearDetail();
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [clearDetail, reportDbId]);

  const loadDetail = useCallback(
    async (recordId: string, page: number, options?: { clearFirst?: boolean }) => {
      const clearFirst = options?.clearFirst ?? true;
      const requestId = ++detailRequestRef.current;
      if (clearFirst) {
        setSelectedDetail(null);
        setDetailError(null);
      }
      setDetailLoading(true);
      setDetailError(null);
      setDetailPage(page);
      try {
        const response = await analyticsHubService.getReportDefinitionSemanticDataById(
          reportDbId,
          recordId,
          { page, page_size: detailPageSize }
        );
        if (requestId !== detailRequestRef.current) return;
        setSelectedDetail(response.data);
        setDetailPage(response.data.pagination?.page ?? page);
      } catch (err) {
        if (requestId !== detailRequestRef.current) return;
        setSelectedDetail(null);
        setDetailError(err instanceof Error ? err.message : 'Failed to load document payload.');
      } finally {
        if (requestId === detailRequestRef.current) {
          setDetailLoading(false);
        }
      }
    },
    [detailPageSize, reportDbId]
  );

  const handleRefreshAll = useCallback(async () => {
    setSearch('');
    setSort('newest');
    setViewScope('document');
    setDisplayMode('tree');
    setPanelMode('browse');
    resetAddForm();
    clearDetail();
    await loadData({ preserveSelection: true });
    setDetailNonce((nonce) => nonce + 1);
    showToast({ message: 'Semantic data refreshed.', type: 'success' });
  }, [clearDetail, loadData, resetAddForm, showToast]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useEffect(() => {
    if (!selectedId) {
      clearDetail();
      return;
    }
    void loadDetail(selectedId, 1, { clearFirst: true });
  }, [selectedId, detailNonce, clearDetail, loadDetail]);

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (panelMode === 'add') {
          setPanelMode('browse');
          resetAddForm();
          return;
        }
        onClose();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose, panelMode, resetAddForm]);

  const entityNames = useMemo(() => {
    const names = new Set([
      ...suggestedEntityNames.filter(Boolean),
      ...records.map((r) => r.entity_name).filter(Boolean),
    ]);
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [records, suggestedEntityNames]);

  const searchFilteredRecords = useMemo(
    () => records.filter((record) => recordMatchesSearch(record, search)),
    [records, search]
  );

  const entityGroups = useMemo(
    () => groupRecordsByEntity(searchFilteredRecords),
    [searchFilteredRecords]
  );

  const selectedEntityGroup = useMemo(
    () => entityGroups.find((group) => group.name === selectedEntityName) ?? null,
    [entityGroups, selectedEntityName]
  );

  const entityDocuments = useMemo(() => {
    if (!selectedEntityGroup) return [];
    return sortRecords(selectedEntityGroup.records, sort);
  }, [selectedEntityGroup, sort]);

  useEffect(() => {
    if (entityGroups.length === 0) {
      setSelectedEntityName(null);
      setSelectedId(null);
      return;
    }
    if (!selectedEntityName || !entityGroups.some((group) => group.name === selectedEntityName)) {
      setSelectedEntityName(entityGroups[0].name);
    }
  }, [entityGroups, selectedEntityName]);

  useEffect(() => {
    if (entityDocuments.length === 0) {
      setSelectedId(null);
      return;
    }
    if (!selectedId || !entityDocuments.some((record) => record._id === selectedId)) {
      setSelectedId(entityDocuments[0]._id);
    }
  }, [entityDocuments, selectedId]);

  const selectedRecord = useMemo(
    () => entityDocuments.find((record) => record._id === selectedId) ?? null,
    [entityDocuments, selectedId]
  );

  const openAddPanel = useCallback(
    (entityName?: string) => {
      resetAddForm();
      if (entityName) {
        setAddEntityName(entityName);
      }
      setPanelMode('add');
    },
    [resetAddForm]
  );

  const selectDocument = useCallback((recordId: string) => {
    setPanelMode('browse');
    if (recordId === selectedId) {
      setDetailNonce((nonce) => nonce + 1);
      return;
    }
    setSelectedId(recordId);
  }, [selectedId]);

  const selectEntity = useCallback((entityName: string) => {
    setSelectedEntityName(entityName);
    setPanelMode('browse');
    clearDetail();
  }, [clearDetail]);

  const goToDetailPage = useCallback(
    (page: number) => {
      if (!selectedId) return;
      const totalPages = selectedDetail?.pagination?.total_pages ?? 0;
      if (page < 1 || (totalPages > 0 && page > totalPages)) return;
      void loadDetail(selectedId, page, { clearFirst: false });
    },
    [loadDetail, selectedDetail?.pagination?.total_pages, selectedId]
  );

  const displayJson = useMemo(() => {
    if (!selectedDetail) return null;
    if (viewScope === 'payload') return selectedDetail.data;
    return {
      _id: selectedDetail._id,
      analytics_id: selectedDetail.analytics_id,
      entity_name: selectedDetail.entity_name,
      submission_datetime: selectedDetail.submission_datetime,
      data: selectedDetail.data,
      data_kind: selectedDetail.data_kind,
      pagination: selectedDetail.pagination,
    };
  }, [selectedDetail, viewScope]);

  const displayJsonText = useMemo(() => {
    if (displayJson === null) return '';
    try {
      return JSON.stringify(displayJson, null, 2);
    } catch {
      return String(displayJson);
    }
  }, [displayJson]);

  const detailPagination = selectedDetail?.pagination ?? null;

  const handleCopy = async () => {
    if (!displayJsonText) return;
    try {
      await navigator.clipboard.writeText(displayJsonText);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  };

  const handleDelete = useCallback(
    async (record: SemanticDataRecord) => {
      const confirmed = window.confirm(
        `Delete semantic data for "${record.entity_name}"?\n\nDocument ID: ${record._id}\n\nThis action cannot be undone.`
      );
      if (!confirmed) return;

      setDeletingId(record._id);
      try {
        const response = await analyticsHubService.deleteReportDefinitionSemanticData(
          reportDbId,
          record._id
        );
        setRecords((prev) => prev.filter((item) => item._id !== record._id));
        if (record._id === selectedId) {
          clearDetail();
          setSelectedId(null);
        }
        if (record.entity_name === selectedEntityName) {
          const remainingForEntity = records.filter(
            (item) => item._id !== record._id && item.entity_name === record.entity_name
          );
          if (remainingForEntity.length === 0) {
            const nextEntity = entityGroups.find((group) => group.name !== record.entity_name);
            setSelectedEntityName(nextEntity?.name ?? null);
          }
        }
        showToast({
          message: response.message || 'Semantic data deleted successfully.',
          type: 'success',
        });
      } catch (err) {
        showToast({
          message: err instanceof Error ? err.message : 'Failed to delete semantic data.',
          type: 'error',
          duration: 6000,
        });
      } finally {
        setDeletingId(null);
      }
    },
    [clearDetail, entityGroups, records, reportDbId, selectedEntityName, selectedId, showToast]
  );

  const handleAddSubmit = useCallback(async () => {
    const trimmedName = addEntityName.trim();
    if (!trimmedName) {
      setAddError('Entity name is required.');
      return;
    }
    if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(trimmedName)) {
      setAddError('Entity name must start with a letter and contain only letters, numbers, and underscores.');
      return;
    }

    const validation = validateEntityJson(addJsonValue);
    if (!validation.valid) {
      setAddError(validation.error ?? 'Invalid JSON.');
      return;
    }

    setAdding(true);
    setAddError(null);
    try {
      const response = await analyticsHubService.createReportDefinitionSemanticData(reportDbId, {
        entityname: trimmedName,
        data: validation.data!,
      });

      const createdId =
        (response.data && '_id' in response.data ? response.data._id : undefined)?.trim() || null;

      setPanelMode('browse');
      resetAddForm();
      setSelectedEntityName(trimmedName);
      await loadData({ preserveSelection: false, silent: true });

      if (createdId) {
        setSelectedId(createdId);
      } else {
        setSelectedEntityName(trimmedName);
      }

      showToast({
        message: response.message || 'Semantic data created successfully.',
        type: 'success',
      });
    } catch (err) {
      setAddError(err instanceof Error ? err.message : 'Failed to create semantic data.');
    } finally {
      setAdding(false);
    }
  }, [addEntityName, addJsonValue, loadData, reportDbId, resetAddForm, showToast]);

  const handleBackdropClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget) onClose();
  };

  const syntaxStyle = theme === 'dark' ? vscDarkPlus : oneLight;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/75 backdrop-blur-md p-4"
      onClick={handleBackdropClick}
      role="presentation"
    >
      <div
        className={`w-[92vw] max-w-7xl h-[82vh] flex flex-col rounded-2xl border ${colors.border} ${
          theme === 'dark' ? 'bg-neutral-900' : 'bg-white'
        } shadow-2xl shadow-black/40 overflow-hidden`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="semantic-data-title"
      >
        {/* Header */}
        <div className={`flex-shrink-0 relative border-b ${colors.border} overflow-hidden`}>
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-600/10 via-teal-600/5 to-transparent pointer-events-none" />
          <div className="relative px-5 py-4 flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-600 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-500/20 shrink-0">
                <Database className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0">
                <h2 id="semantic-data-title" className={`text-base font-semibold ${colors.text.primary}`}>
                  Entity Semantic Data
                </h2>
                <p className={`text-[11px] ${colors.text.muted} truncate`}>
                  {reportName ? `${reportName} · ` : ''}
                  {collectionName} · {entityGroups.length} entit{entityGroups.length === 1 ? 'y' : 'ies'} · {records.length} document{records.length === 1 ? '' : 's'}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <div className="relative">
                <Search className={`absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${colors.text.muted}`} />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search entities or id…"
                  className={`w-52 pl-8 pr-3 py-1.5 rounded-lg border text-[11px] focus:outline-none focus:ring-2 focus:ring-emerald-500/30 ${colors.input}`}
                />
              </div>

              <div className="flex items-center gap-1.5">
                <ArrowUpDown className={`w-3.5 h-3.5 ${colors.text.muted}`} />
                <select
                  value={sort}
                  onChange={(e) => setSort(e.target.value as SortOption)}
                  className={`py-1.5 pl-2 pr-7 rounded-lg border text-[11px] focus:outline-none focus:ring-2 focus:ring-emerald-500/30 ${colors.input}`}
                  aria-label="Sort documents"
                >
                  <option value="newest">Newest first</option>
                  <option value="oldest">Oldest first</option>
                </select>
              </div>

              <button
                type="button"
                onClick={() => openAddPanel(selectedEntityName ?? undefined)}
                disabled={loading || refreshing || adding}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-medium bg-emerald-600 hover:bg-emerald-700 text-white transition-colors disabled:opacity-50"
                title="Add semantic data"
              >
                <Plus className="w-3.5 h-3.5" />
                Add
              </button>

              <button
                type="button"
                onClick={() => void handleRefreshAll()}
                disabled={loading || refreshing || adding}
                className={`p-2 rounded-lg border ${colors.border} ${colors.hover} ${colors.text.muted} transition-colors disabled:opacity-50`}
                title="Refresh entire modal"
                aria-label="Refresh entire modal"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${loading || refreshing ? 'animate-spin' : ''}`} />
              </button>

              <button
                type="button"
                onClick={onClose}
                className={`p-2 rounded-lg ${colors.hover} ${colors.text.muted} hover:text-red-400 transition-colors`}
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 flex min-h-0 overflow-hidden">
          {/* Entities (MongoDB collections-style) */}
          <div
            className={`w-52 shrink-0 flex flex-col border-r ${colors.border} ${theme === 'dark' ? 'bg-neutral-950/60' : 'bg-gray-50/90'}`}
          >
            <div className={`px-3 py-2 border-b ${colors.border}`}>
              <span className={`text-[10px] font-semibold uppercase tracking-wider ${colors.text.muted}`}>
                Entities
              </span>
            </div>

            <div className="flex-1 overflow-y-auto custom-scrollbar p-2 space-y-1">
              {loading && (
                <div className="flex flex-col items-center justify-center py-10 gap-2">
                  <Loader2 className="w-5 h-5 animate-spin text-emerald-400" />
                </div>
              )}

              {!loading && error && (
                <div className="p-2 rounded-lg border border-red-500/30 bg-red-500/10">
                  <p className="text-[10px] text-red-400">{error}</p>
                </div>
              )}

              {!loading && !error && entityGroups.length === 0 && (
                <div className="flex flex-col items-center justify-center py-10 px-3 text-center">
                  <FolderOpen className={`w-7 h-7 mb-2 ${colors.text.muted}`} />
                  <p className={`text-[10px] ${colors.text.muted}`}>No entities found</p>
                </div>
              )}

              {!loading &&
                !error &&
                entityGroups.map((group) => {
                  const isSelected = group.name === selectedEntityName;
                  return (
                    <button
                      key={group.name}
                      type="button"
                      onClick={() => {
                        selectEntity(group.name);
                      }}
                      className={`w-full text-left rounded-lg border px-2.5 py-2 transition-all ${
                        isSelected
                          ? 'border-emerald-500/50 bg-emerald-500/10 ring-1 ring-emerald-500/20'
                          : `${colors.border} ${colors.hover} hover:border-emerald-500/20`
                      }`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <FolderOpen className={`w-3.5 h-3.5 shrink-0 ${isSelected ? 'text-emerald-400' : colors.text.muted}`} />
                        <span className={`text-[11px] font-semibold truncate ${isSelected ? 'text-emerald-300' : colors.text.primary}`}>
                          {group.name}
                        </span>
                        <span className={`ml-auto shrink-0 text-[9px] px-1.5 py-0.5 rounded-full border ${colors.border} ${colors.text.muted}`}>
                          {group.count}
                        </span>
                      </div>
                      <p className={`text-[9px] ${colors.text.muted} mt-1 pl-5 truncate`}>
                        Latest · {formatTimestamp(group.latestSubmission)}
                      </p>
                    </button>
                  );
                })}
            </div>
          </div>

          {panelMode === 'add' ? (
            <div className="flex-1 flex flex-col min-w-0">
              <div
                className={`flex-shrink-0 flex items-center justify-between gap-2 px-4 py-2 border-b ${colors.border} ${colors.secondary}`}
              >
                <div className="flex items-center gap-2">
                  <Plus className="w-4 h-4 text-emerald-400" />
                  <div>
                    <p className={`text-xs font-semibold ${colors.text.primary}`}>Add Semantic Data</p>
                    <p className={`text-[10px] ${colors.text.muted}`}>
                      POST to {collectionName} for this report
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setPanelMode('browse');
                    resetAddForm();
                  }}
                  disabled={adding}
                  className={`px-2 py-1 rounded text-[10px] ${colors.text.muted} ${colors.hover} border ${colors.border}`}
                >
                  Back to browse
                </button>
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar p-5">
                <div className="max-w-2xl mx-auto space-y-4">
                  <div>
                    <label
                      htmlFor="semantic-entity-name"
                      className={`block text-[10px] font-medium uppercase tracking-wide ${colors.text.muted} mb-1.5`}
                    >
                      Entity name
                    </label>
                    <input
                      id="semantic-entity-name"
                      type="text"
                      list="semantic-entity-suggestions"
                      value={addEntityName}
                      onChange={(e) => {
                        setAddEntityName(e.target.value);
                        setAddError(null);
                      }}
                      placeholder="e.g. salesData"
                      className={`w-full px-3 py-2 rounded-lg border text-[12px] focus:outline-none focus:ring-2 focus:ring-emerald-500/30 ${colors.input}`}
                    />
                    <datalist id="semantic-entity-suggestions">
                      {entityNames.map((name) => (
                        <option key={name} value={name} />
                      ))}
                    </datalist>
                    <p className={`text-[10px] ${colors.text.muted} mt-1`}>
                      Maps to <code className="text-emerald-400">entityname</code> in the API payload.
                    </p>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label
                        htmlFor="semantic-data-json"
                        className={`text-[10px] font-medium uppercase tracking-wide ${colors.text.muted} flex items-center gap-1`}
                      >
                        <Braces className="w-3 h-3" />
                        Data (JSON object or array)
                      </label>
                      <button
                        type="button"
                        onClick={() => {
                          setAddJsonValue(SAMPLE_SEMANTIC_DATA_JSON);
                          setAddError(null);
                        }}
                        className="text-[10px] text-emerald-400 hover:text-emerald-300 flex items-center gap-0.5"
                      >
                        <Sparkles className="w-2.5 h-2.5" />
                        Load sample
                      </button>
                    </div>
                    <textarea
                      id="semantic-data-json"
                      value={addJsonValue}
                      onChange={(e) => {
                        setAddJsonValue(e.target.value);
                        setAddError(null);
                      }}
                      rows={16}
                      spellCheck={false}
                      className={`w-full px-3 py-2 rounded-lg border text-[11px] font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-emerald-500/30 resize-none ${colors.input}`}
                    />
                  </div>

                  {addError && (
                    <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30">
                      <AlertCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                      <p className="text-[11px] text-red-400">{addError}</p>
                    </div>
                  )}

                  <div className="flex items-center justify-end gap-2 pt-2">
                    <button
                      type="button"
                      onClick={() => {
                        setPanelMode('browse');
                        resetAddForm();
                      }}
                      disabled={adding}
                      className={`px-3 py-1.5 rounded-lg text-[11px] ${colors.text.secondary} ${colors.hover} border ${colors.border} disabled:opacity-50`}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleAddSubmit()}
                      disabled={adding}
                      className="px-4 py-1.5 rounded-lg text-[11px] bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-medium flex items-center gap-1.5"
                    >
                      {adding && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                      Submit document
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Documents for selected entity */}
              <div
                className={`w-64 shrink-0 flex flex-col border-r ${colors.border} ${theme === 'dark' ? 'bg-neutral-950/40' : 'bg-gray-50/60'}`}
              >
                <div className={`px-3 py-2 border-b ${colors.border} flex items-center justify-between gap-2`}>
                  <div className="min-w-0">
                    <span className={`text-[10px] font-semibold uppercase tracking-wider ${colors.text.muted}`}>
                      Documents
                    </span>
                    {selectedEntityName && (
                      <p className={`text-[10px] text-emerald-400 truncate mt-0.5`}>{selectedEntityName}</p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => openAddPanel(selectedEntityName ?? undefined)}
                    disabled={loading || refreshing || adding}
                    className={`p-1 rounded-md border ${colors.border} ${colors.hover} text-emerald-400 transition-colors disabled:opacity-40`}
                    title="Add document to entity"
                    aria-label="Add document to entity"
                  >
                    <Plus className="w-3 h-3" />
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto custom-scrollbar p-2 space-y-1.5">
                  {!selectedEntityName && !loading && (
                    <div className="flex flex-col items-center justify-center py-12 px-3 text-center">
                      <Layers className={`w-7 h-7 mb-2 ${colors.text.muted}`} />
                      <p className={`text-[10px] ${colors.text.muted}`}>Select an entity</p>
                    </div>
                  )}

                  {selectedEntityName && entityDocuments.length === 0 && !loading && (
                    <div className="flex flex-col items-center justify-center py-12 px-3 text-center">
                      <FileJson className={`w-7 h-7 mb-2 ${colors.text.muted}`} />
                      <p className={`text-[10px] font-medium ${colors.text.secondary}`}>No documents</p>
                      <p className={`text-[9px] ${colors.text.muted} mt-1`}>Add data for this entity</p>
                    </div>
                  )}

                  {selectedEntityName &&
                    entityDocuments.map((record, index) => {
                      const isSelected = record._id === selectedId;
                      const isDeleting = deletingId === record._id;
                      return (
                        <div
                          key={record._id}
                          className={`group rounded-lg border transition-all ${
                            isSelected
                              ? 'border-indigo-500/50 bg-indigo-500/10 ring-1 ring-indigo-500/20'
                              : `${colors.border} ${colors.hover} hover:border-indigo-500/20`
                          }`}
                        >
                          <div className="flex items-start gap-1 p-2.5">
                            <button
                              type="button"
                              onClick={() => selectDocument(record._id)}
                              className="flex-1 min-w-0 text-left"
                            >
                              <div className="flex items-center gap-1.5 mb-1">
                                <span className={`text-[10px] font-semibold ${isSelected ? 'text-indigo-300' : colors.text.primary}`}>
                                  #{sort === 'newest' ? entityDocuments.length - index : index + 1}
                                </span>
                                <span className={`text-[9px] ${colors.text.muted}`}>·</span>
                                <Calendar className={`w-3 h-3 shrink-0 ${colors.text.muted}`} />
                                <span className={`text-[9px] ${colors.text.muted} truncate`}>
                                  {formatTimestamp(record.submission_datetime)}
                                </span>
                                {typeof record.item_count === 'number' && (
                                  <>
                                    <span className={`text-[9px] ${colors.text.muted}`}>·</span>
                                    <span className={`text-[9px] ${colors.text.muted}`}>
                                      {record.item_count} item{record.item_count === 1 ? '' : 's'}
                                    </span>
                                  </>
                                )}
                              </div>
                              <p className={`text-[9px] font-mono leading-snug line-clamp-2 ${colors.text.secondary}`}>
                                {getRecordPreview(record)}
                              </p>
                            </button>
                            <button
                              type="button"
                              onClick={() => void handleDelete(record)}
                              disabled={isDeleting || deletingId !== null}
                              className={`p-1 rounded-md shrink-0 transition-all opacity-100 sm:opacity-0 sm:group-hover:opacity-100 focus:opacity-100 disabled:opacity-40 ${
                                isDeleting
                                  ? 'text-red-400'
                                  : `${colors.text.muted} hover:text-red-400 hover:bg-red-500/10`
                              }`}
                              title="Delete document"
                              aria-label={`Delete document ${index + 1}`}
                            >
                              {isDeleting ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <Trash2 className="w-3.5 h-3.5" />
                              )}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>

              {/* JSON viewer */}
              <div className="flex-1 flex flex-col min-w-0">
                <div
                  className={`flex-shrink-0 flex flex-wrap items-center justify-between gap-2 px-4 py-2 border-b ${colors.border} ${colors.secondary}`}
                >
                  <div className="flex items-center gap-1 p-0.5 rounded-md border border-neutral-700/50 bg-neutral-900/40">
                    <button
                      type="button"
                      onClick={() => setViewScope('document')}
                      className={`px-2 py-1 rounded text-[10px] font-medium transition-colors flex items-center gap-1 ${
                        viewScope === 'document'
                          ? 'bg-emerald-600 text-white'
                          : `${colors.text.muted} ${colors.hover}`
                      }`}
                    >
                      <FileJson className="w-3 h-3" />
                      Document
                    </button>
                    <button
                      type="button"
                      onClick={() => setViewScope('payload')}
                      className={`px-2 py-1 rounded text-[10px] font-medium transition-colors flex items-center gap-1 ${
                        viewScope === 'payload'
                          ? 'bg-emerald-600 text-white'
                          : `${colors.text.muted} ${colors.hover}`
                      }`}
                    >
                      <Braces className="w-3 h-3" />
                      Data payload
                    </button>
                  </div>

                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1 p-0.5 rounded-md border border-neutral-700/50 bg-neutral-900/40">
                      <button
                        type="button"
                        onClick={() => setDisplayMode('tree')}
                        className={`px-2 py-1 rounded text-[10px] font-medium transition-colors ${
                          displayMode === 'tree'
                            ? 'bg-indigo-600 text-white'
                            : `${colors.text.muted} ${colors.hover}`
                        }`}
                      >
                        Tree
                      </button>
                      <button
                        type="button"
                        onClick={() => setDisplayMode('raw')}
                        className={`px-2 py-1 rounded text-[10px] font-medium transition-colors ${
                          displayMode === 'raw'
                            ? 'bg-indigo-600 text-white'
                            : `${colors.text.muted} ${colors.hover}`
                        }`}
                      >
                        Raw JSON
                      </button>
                    </div>

                    {displayMode === 'tree' && (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            setTreeDefaultExpanded(true);
                            setTreeKey((key) => key + 1);
                          }}
                          className={`px-2 py-1 rounded text-[10px] ${colors.text.muted} ${colors.hover} border ${colors.border}`}
                        >
                          Expand all
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setTreeDefaultExpanded(false);
                            setTreeKey((key) => key + 1);
                          }}
                          className={`px-2 py-1 rounded text-[10px] ${colors.text.muted} ${colors.hover} border ${colors.border}`}
                        >
                          Collapse all
                        </button>
                      </>
                    )}

                    <button
                      type="button"
                      onClick={() => void handleCopy()}
                      disabled={!displayJsonText || detailLoading}
                      className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium border ${colors.border} ${colors.hover} ${
                        copied ? 'text-emerald-400 border-emerald-500/30' : colors.text.muted
                      } disabled:opacity-40`}
                    >
                      {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                      {copied ? 'Copied' : 'Copy'}
                    </button>

                    {detailPagination && detailPagination.total_pages > 0 && (
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => goToDetailPage(detailPage - 1)}
                          disabled={detailLoading || detailPage <= 1}
                          className={`p-1 rounded border ${colors.border} ${colors.hover} ${colors.text.muted} disabled:opacity-40`}
                          title="Previous page"
                          aria-label="Previous page"
                        >
                          <ChevronLeft className="w-3.5 h-3.5" />
                        </button>
                        <span className={`text-[10px] tabular-nums px-1 ${colors.text.secondary}`}>
                          Page {detailPagination.page} / {detailPagination.total_pages}
                          <span className={`ml-1 ${colors.text.muted}`}>
                            ({detailPagination.total_items} item{detailPagination.total_items === 1 ? '' : 's'})
                          </span>
                        </span>
                        <button
                          type="button"
                          onClick={() => goToDetailPage(detailPage + 1)}
                          disabled={
                            detailLoading ||
                            detailPagination.total_pages === 0 ||
                            detailPage >= detailPagination.total_pages
                          }
                          className={`p-1 rounded border ${colors.border} ${colors.hover} ${colors.text.muted} disabled:opacity-40`}
                          title="Next page"
                          aria-label="Next page"
                        >
                          <ChevronRight className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}

                    {selectedRecord && (
                      <button
                        type="button"
                        onClick={() => void handleDelete(selectedRecord)}
                        disabled={deletingId !== null}
                        className="flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium border border-red-500/30 hover:bg-red-500/10 text-red-400 transition-colors disabled:opacity-40"
                        title="Delete selected document"
                        aria-label="Delete selected document"
                      >
                        {deletingId === selectedRecord._id ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <Trash2 className="w-3 h-3" />
                        )}
                        Delete
                      </button>
                    )}
                  </div>
                </div>

                <div
                  className={`flex-1 min-h-0 overflow-auto custom-scrollbar ${
                    theme === 'dark' ? 'bg-neutral-950' : 'bg-gray-50'
                  }`}
                >
              {!selectedId && !loading && (
                <div className="flex flex-col items-center justify-center h-full py-16">
                  <Braces className={`w-10 h-10 mb-3 ${colors.text.muted}`} />
                  <p className={`text-sm ${colors.text.muted}`}>Select a document to inspect</p>
                </div>
              )}

              {selectedId && detailLoading && !selectedDetail && (
                <div className="flex flex-col items-center justify-center h-full py-16 gap-2">
                  <Loader2 className="w-6 h-6 animate-spin text-emerald-400" />
                  <p className={`text-sm ${colors.text.muted}`}>Loading document payload…</p>
                </div>
              )}

              {selectedId && detailError && !detailLoading && (
                <div className="flex flex-col items-center justify-center h-full py-16 px-6 text-center">
                  <AlertCircle className="w-8 h-8 mb-3 text-red-400" />
                  <p className="text-sm text-red-400">{detailError}</p>
                  <button
                    type="button"
                    onClick={() => selectedId && void loadDetail(selectedId, detailPage, { clearFirst: true })}
                    className="mt-3 px-3 py-1.5 rounded-lg text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    Retry
                  </button>
                </div>
              )}

              {selectedDetail && detailLoading && (
                <div className={`sticky top-0 z-10 px-4 py-1.5 text-[10px] border-b ${colors.border} ${colors.secondary} ${colors.text.muted} flex items-center gap-1.5`}>
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Loading page {detailPage}…
                </div>
              )}

              {selectedDetail && displayMode === 'tree' && displayJson !== null && (
                <div className="p-4">
                  <JsonTreeNode
                    key={`${treeKey}-${selectedDetail._id}-${detailPage}`}
                    value={displayJson}
                    depth={0}
                    theme={theme}
                    defaultExpanded={treeDefaultExpanded}
                  />
                </div>
              )}

              {selectedDetail && displayMode === 'raw' && displayJsonText && (
                <SyntaxHighlighter
                  language="json"
                  style={syntaxStyle}
                  customStyle={{
                    margin: 0,
                    padding: '1rem 1.25rem',
                    background: 'transparent',
                    fontSize: 11,
                    lineHeight: 1.55,
                    minHeight: '100%',
                  }}
                  showLineNumbers
                  lineNumberStyle={{
                    minWidth: '2.5em',
                    paddingRight: '1em',
                    color: theme === 'dark' ? '#525252' : '#9ca3af',
                    userSelect: 'none',
                  }}
                  codeTagProps={{
                    style: {
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                    },
                  }}
                >
                  {displayJsonText}
                </SyntaxHighlighter>
              )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportSemanticDataModal;
